import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Icons
import {
  FileText,
  Wand2,
  Save,
  Play,
  Mic,
  Video,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Clock,
  Trash2,
  Plus,
  Download,
  Volume2
} from "lucide-react";

const ScriptEditor = () => {
  const { dayNumber } = useParams();
  const navigate = useNavigate();
  
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingVoice, setGeneratingVoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [audioPlayer, setAudioPlayer] = useState(null);
  
  // Form state
  const [title, setTitle] = useState("");
  const [openingMessage, setOpeningMessage] = useState("");
  const [scriptContent, setScriptContent] = useState("");
  const [status, setStatus] = useState("draft");
  const [newDayNumber, setNewDayNumber] = useState(1);

  useEffect(() => {
    fetchScripts();
  }, []);

  useEffect(() => {
    if (dayNumber && scripts.length > 0) {
      const script = scripts.find(s => s.day_number === parseInt(dayNumber));
      if (script) {
        selectScript(script);
      }
    }
  }, [dayNumber, scripts]);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/videos?limit=365');
      setScripts(response.data);
      
      // If dayNumber is provided, select that script
      if (dayNumber) {
        const script = response.data.find(s => s.day_number === parseInt(dayNumber));
        if (script) {
          selectScript(script);
        }
      } else if (response.data.length > 0) {
        selectScript(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch scripts:', error);
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  const selectScript = (script) => {
    setSelectedScript(script);
    setTitle(script.title || "");
    setOpeningMessage(script.opening_message || "");
    setScriptContent(script.script_content || "");
    setStatus(script.status || "draft");
  };

  const generateScript = async (day) => {
    try {
      setGenerating(true);
      const response = await api.post('/videos/generate-script', { day_number: day });
      toast.success(`Script for Day ${day} generated!`);
      
      // Refresh scripts list
      await fetchScripts();
      selectScript(response.data);
      navigate(`/scripts/${day}`);
    } catch (error) {
      console.error('Failed to generate script:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate script');
    } finally {
      setGenerating(false);
    }
  };

  const generateVoice = async () => {
    if (!selectedScript) return;
    
    try {
      setGeneratingVoice(true);
      const response = await api.post('/videos/generate-voice', {
        video_id: selectedScript.id,
        voice: "onyx"
      });
      
      toast.success('Voice generated successfully!');
      
      // Play the audio
      if (response.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
        setAudioPlayer(audio);
        audio.play();
      }
      
      // Refresh scripts
      await fetchScripts();
    } catch (error) {
      console.error('Failed to generate voice:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate voice');
    } finally {
      setGeneratingVoice(false);
    }
  };

  const saveScript = async () => {
    if (!selectedScript) return;
    
    try {
      setSaving(true);
      await api.put(`/videos/${selectedScript.id}`, {
        title,
        opening_message: openingMessage,
        script_content: scriptContent,
        status
      });
      
      toast.success('Script saved!');
      await fetchScripts();
    } catch (error) {
      console.error('Failed to save script:', error);
      toast.error('Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  const batchGenerate = async (startDay, endDay) => {
    try {
      setBatchGenerating(true);
      const response = await api.post(`/videos/batch-generate-scripts?start_day=${startDay}&end_day=${endDay}`);
      
      toast.success(`Generated ${response.data.total_generated} scripts!`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} scripts failed to generate`);
      }
      
      await fetchScripts();
    } catch (error) {
      console.error('Failed to batch generate:', error);
      toast.error(error.response?.data?.detail || 'Failed to batch generate');
    } finally {
      setBatchGenerating(false);
    }
  };

  const getStatusBadge = (scriptStatus) => {
    const styles = {
      draft: "status-badge status-draft",
      approved: "status-badge status-approved",
      generating: "status-badge status-generating",
      completed: "status-badge status-completed"
    };
    return <span className={styles[scriptStatus] || styles.draft}>{scriptStatus}</span>;
  };

  const navigateDay = (direction) => {
    if (!selectedScript) return;
    const currentDay = selectedScript.day_number;
    const newDay = direction === 'prev' ? currentDay - 1 : currentDay + 1;
    
    if (newDay >= 1 && newDay <= 365) {
      const script = scripts.find(s => s.day_number === newDay);
      if (script) {
        navigate(`/scripts/${newDay}`);
        selectScript(script);
      } else {
        // Generate script for this day
        generateScript(newDay);
      }
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-fade-in" data-testid="scripts-loading">
        <div className="lg:col-span-1">
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
        <div className="lg:col-span-3">
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="script-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">
            Script Editor
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage Abraham's daily video scripts
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="batch-generate-btn">
                <Plus className="w-4 h-4" />
                Batch Generate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Batch Generate Scripts</DialogTitle>
                <DialogDescription>
                  Generate multiple scripts at once using AI
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium">Start Day</label>
                    <Input 
                      type="number" 
                      min={1} 
                      max={365}
                      defaultValue={1}
                      id="batch-start"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium">End Day</label>
                    <Input 
                      type="number" 
                      min={1} 
                      max={365}
                      defaultValue={10}
                      id="batch-end"
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Maximum 30 scripts per batch. This may take several minutes.
                </p>
              </div>
              <DialogFooter>
                <Button 
                  onClick={() => {
                    const start = parseInt(document.getElementById('batch-start').value);
                    const end = parseInt(document.getElementById('batch-end').value);
                    batchGenerate(start, end);
                  }}
                  disabled={batchGenerating}
                  className="gap-2"
                >
                  {batchGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Scripts
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Scripts List */}
        <Card className="card-patriarch lg:col-span-1" data-testid="scripts-list">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-display">Scripts</CardTitle>
              <Badge variant="outline">{scripts.length}/365</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="p-4 space-y-1">
                {/* Generate New Script Button */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2 mb-2"
                      data-testid="new-script-btn"
                    >
                      <Plus className="w-4 h-4" />
                      New Script
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Generate New Script</DialogTitle>
                      <DialogDescription>
                        Enter the day number for the new script
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <label className="text-sm font-medium">Day Number (1-365)</label>
                      <Input 
                        type="number" 
                        min={1} 
                        max={365}
                        value={newDayNumber}
                        onChange={(e) => setNewDayNumber(parseInt(e.target.value))}
                        className="mt-2"
                      />
                    </div>
                    <DialogFooter>
                      <Button 
                        onClick={() => generateScript(newDayNumber)}
                        disabled={generating}
                        className="gap-2"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4" />
                            Generate
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Separator className="my-2" />

                {scripts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No scripts yet</p>
                  </div>
                ) : (
                  scripts.map((script) => (
                    <button
                      key={script.id}
                      onClick={() => {
                        navigate(`/scripts/${script.day_number}`);
                        selectScript(script);
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedScript?.id === script.id
                          ? 'bg-accent/20 border border-accent/30'
                          : 'hover:bg-muted/50'
                      }`}
                      data-testid={`script-list-item-${script.day_number}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-accent/20 flex items-center justify-center flex-shrink-0">
                          <span className="font-display font-bold text-sm text-accent">
                            {script.day_number}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{script.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(script.status)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Editor */}
        <Card className="card-patriarch lg:col-span-3" data-testid="script-editor-panel">
          {selectedScript ? (
            <>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigateDay('prev')}
                      disabled={selectedScript.day_number <= 1}
                      data-testid="prev-day-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <div>
                      <CardTitle className="font-display">Day {selectedScript.day_number}</CardTitle>
                      <CardDescription>{selectedScript.scheduled_date}</CardDescription>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigateDay('next')}
                      disabled={selectedScript.day_number >= 365}
                      data-testid="next-day-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="w-[130px]" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      className="gap-2"
                      onClick={saveScript}
                      disabled={saving}
                      data-testid="save-script-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Title */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Title</label>
                  <Input 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter script title..."
                    className="font-display"
                    data-testid="script-title-input"
                  />
                </div>

                {/* Opening Message (for Day 2+) */}
                {selectedScript.day_number > 1 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Daily Wisdom Message
                      <span className="text-muted-foreground font-normal ml-2">
                        (Opening reminder to be good)
                      </span>
                    </label>
                    <Textarea 
                      value={openingMessage}
                      onChange={(e) => setOpeningMessage(e.target.value)}
                      placeholder="Enter a brief message of wisdom..."
                      className="min-h-[80px] font-display italic"
                      data-testid="opening-message-input"
                    />
                  </div>
                )}

                {/* Script Content */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Script Content</label>
                  <Textarea 
                    value={scriptContent}
                    onChange={(e) => setScriptContent(e.target.value)}
                    placeholder="Write or generate the script content..."
                    className="script-editor min-h-[300px]"
                    data-testid="script-content-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Word count: ~{scriptContent.split(/\s+/).filter(Boolean).length} words
                    ({Math.round(scriptContent.split(/\s+/).filter(Boolean).length / 150)} min read)
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => generateScript(selectedScript.day_number)}
                    disabled={generating}
                    className="gap-2"
                    data-testid="regenerate-script-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Regenerate Script
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={generateVoice}
                    disabled={generatingVoice || !scriptContent}
                    className="gap-2"
                    data-testid="generate-voice-btn"
                  >
                    {generatingVoice ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating Voice...
                      </>
                    ) : (
                      <>
                        <Mic className="w-4 h-4" />
                        Generate Voice
                      </>
                    )}
                  </Button>

                  {selectedScript.voice_url && (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${selectedScript.voice_url}`);
                        setAudioPlayer(audio);
                        audio.play();
                      }}
                      className="gap-2"
                      data-testid="play-voice-btn"
                    >
                      <Volume2 className="w-4 h-4" />
                      Play Voice
                    </Button>
                  )}

                  <Link to="/generate">
                    <Button variant="outline" className="gap-2" data-testid="generate-video-link">
                      <Video className="w-4 h-4" />
                      Generate Video
                    </Button>
                  </Link>
                </div>

                {/* Themes and References */}
                {(selectedScript.themes?.length > 0 || selectedScript.bible_references?.length > 0) && (
                  <div className="flex flex-wrap gap-4 pt-4 border-t">
                    {selectedScript.themes?.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Themes</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedScript.themes.map((theme, i) => (
                            <Badge key={i} variant="secondary">{theme}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedScript.bible_references?.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bible References</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedScript.bible_references.map((ref, i) => (
                            <Badge key={i} variant="outline">{ref}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center h-[500px] text-muted-foreground">
              <FileText className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-display">Select a script to edit</p>
              <p className="text-sm">Or generate a new one to get started</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ScriptEditor;
